/**
 * The before/during choice for a service that crosses mid-event.
 *
 * **This state is local to the browser and is never sent anywhere**, because
 * there is nowhere to send it: no route accepts it, no contract carries it, and
 * the `items` table has no column for it (§2). The API tells us a crossing
 * *offers* the choice — `TimelineOccurrence.choice`, true when a service lands
 * partway through an event — but it has no opinion about which the user picked.
 *
 * So the picker works, and the timeline and dashboard react to it, and it is
 * forgotten on reload. That is a real gap rather than a design: persisting it
 * needs an API change (a `plan` on the item, or a per-occurrence override),
 * which is out of scope here. It lives in one module, behind one context, so
 * that when the route exists this is the only file that has to change.
 *
 * Deliberately *not* persisted to localStorage: a choice that survives a reload
 * but is invisible to the crew member standing next to you is worse than one
 * that plainly resets — it looks like a shared plan and is not.
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type Plan = 'before' | 'during';

/** Service in the shop, not between rounds — the safer default. */
export const DEFAULT_PLAN: Plan = 'before';

interface ScheduleState {
  planFor: (itemId: string) => Plan;
  setPlan: (itemId: string, plan: Plan) => void;
}

const ScheduleContext = createContext<ScheduleState | null>(null);

export function ScheduleProvider({ children }: { children: ReactNode }) {
  const [plans, setPlans] = useState<Record<string, Plan>>({});

  const planFor = useCallback((itemId: string): Plan => plans[itemId] ?? DEFAULT_PLAN, [plans]);
  const setPlan = useCallback((itemId: string, plan: Plan) => setPlans((p) => ({ ...p, [itemId]: plan })), []);

  const value = useMemo(() => ({ planFor, setPlan }), [planFor, setPlan]);
  return <ScheduleContext.Provider value={value}>{children}</ScheduleContext.Provider>;
}

export function useSchedule(): ScheduleState {
  const context = useContext(ScheduleContext);
  if (!context) throw new Error('useSchedule() used outside <ScheduleProvider>');
  return context;
}
